<template>
  <el-dialog
    :custom-class="'dialog-dept'"
    :visible.sync="isVisible"
    :show-close="false"
    :close-on-press-escape="false"
    :close-on-click-modal="closeOnClickModal"
    @open="handleOnOpen"
  >
    <div class="dept-body" :class="{'single-mode': !multiple}">
      <div class="dept-wrap">
        <div class="search-form">
          <el-input
            ref="searchInput"
            v-model="keyword"
            placeholder="请输入"
            prefix-icon="el-icon-search"
            size="small"
            class="search-input"
          />
          <div v-show="keyword" class="clear-action" @click="resetSearchForm()">
            <i class="el-icon-close" />
          </div>
        </div>
        <div id="dept-content" class="dept-content">
          <department-tree
            ref="departmentTree"
            v-show="!isSearchResultVisible"
            :disabled-user-ids="disabledUserIds"
            :selected-members="selectedMembers"
            @onMemberCheck="onMemberCheck"
          />
          <search-member-list
            v-if="isSearchResultVisible"
            ref="searchMembers"
            v-loading="searching && !searchMembers.length"
            :disabled-user-ids="disabledUserIds"
            :loading="searching"
            :keyword="keyword"
            :search-members="searchMembers"
            @onSearchResultScroll="onSearchResultScroll"
            @onMemberCheck="onMemberCheck"
          />
        </div>
      </div>
      <selected-member-list
        :selected-members="selectedMembers"
        @onRemoveMember="onRemoveMember"
        @onOk="onOk"
        @onCancel="onCancel()"
      />
    </div>
  </el-dialog>
</template>

<script>
import DepartmentTree from './DepartmentTree.vue'
import SearchMemberList from './SearchMemberList.vue'
import SelectedMemberList from './SelectedMemberList.vue'
import { getCompanyMemberList, setCompanyId } from '../utils/get-list.js'
import { debounce } from 'lodash'
import { sendMessage, sendNetworkFailedMessage } from '../utils/send-message'

export default {
  name: 'DepartmentDialog',
  components: {
    DepartmentTree,
    SearchMemberList,
    SelectedMemberList
  },
  props: {
    data: {
      type: Array,
      default: () => Array.from({ length: 0 })
    },
    disabledUserIds: {
      type: Array,
      default: () => Array.from({ length: 0 })
    },
    title: {
      type: String,
      default: ''
    },
    visible: {
      type: Boolean,
      default: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    limit: {
      type: Number,
      default: -1
    },
    closeOnClickModal: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      searching: false,
      isSearchResultVisible: false,
      keyword: '',
      selectedMembers: Array.from({ length: 0 }),
      searchMembers: Array.from({ length: 0 }),
      searchPagination: {
        total: 0,
        totalPage: 0,
        currentPage: 1,
        pageSize: 20
      },
      debounceKeywordChange: null
    }
  },
  computed: {
    isVisible: {
      get () {
        return this.visible
      },
      set (value) {
        this.$emit('update:visible', value)
      }
    },
    curEnterprise () {
      return this.$store.getters['User/curEnterprise']
    }
  },
  watch: {
    keyword (newVal, oldVal) {
      if (!newVal) {
        this.isSearchResultVisible = false
      }
      this.debounceKeywordChange()
    },
    visible (newVal) {
      if (newVal && this.$refs.departmentTree) {
        this.$refs.departmentTree.initTreeCheckStatus()
        this.selectedMembers = []
      }
    }
  },
  updated () {
    setCompanyId(this.curEnterprise.company_id)
  },
  created () {
    this.debounceKeywordChange = debounce(this.onKeywordChange, 300)
  },
  mounted () {
    this.selectedMembers = this.data.map((t) => {
      const member = {
        id: t.id,
        name: t.name,
        avatar: t.avatar,
        email: t.email,
        phone: t.phone,
        dept: t.dept,
        isHover: t.isHover
      }
      return member
    })
  },
  methods: {
    onKeywordChange (e) {
      if (!this.keyword) {
        this.isSearchResultVisible = false
        return
      }
      this.searchPagination = {
        total: 0,
        totalPage: 0,
        currentPage: 1,
        pageSize: 20
      }
      this.searchMembers = []
      this.isSearchResultVisible = true
      this.searching = true
      this.loadCompanyMember()
    },
    onSearchResultScroll (e) {
      const deptContainer = document.getElementById('dept-content')
      const memberListContainer = document.getElementById('member-list')
      const scrollTop = e.currentTarget.scrollTop

      if ((deptContainer.offsetHeight + scrollTop - 20 > memberListContainer.offsetHeight) &&
        !this.searching &&
        this.searchPagination.currentPage < this.searchPagination.totalPage) {
        this.searchPagination.currentPage += 1
        this.loadCompanyMember()
      }
    },
    async loadCompanyMember () {
      this.searching = true
      const params = {
        count: this.searchPagination.pageSize,
        offset: this.searchPagination.currentPage - 1,
        searchname: this.keyword
      }
      try {
        const res = await getCompanyMemberList(params)
        const resData = res.data
        const selectedMemberIds = this.selectedMembers.map(t => t.id)
        const memberList = resData.members.map((t) => {
          return {
            id: t.id,
            name: t.name,
            email: t.email,
            phone: t.phone,
            dept: t.dept_path,
            avatar: t.avatar,
            isChecked: selectedMemberIds.indexOf(t.id) > -1
          }
        })
        this.searchMembers = [...this.searchMembers, ...memberList]
        this.searchPagination.totalPage = Math.ceil(resData.total / this.searchPagination.pageSize)
        this.searching = false
      } catch (error) {
        sendNetworkFailedMessage()
        this.searching = false
      }
    },
    resetSearchForm () {
      this.keyword = ''
      this.searchPagination = {
        total: 0,
        totalPage: 0,
        currentPage: 1,
        pageSize: 20
      }
      this.searchMembers = []
      this.isSearchResultVisible = false
    },
    onRemoveMember (data) {
      this.selectedMembers = this.selectedMembers.filter(t => t.id !== data.id)
      const member = this.searchMembers.find(t => t.id === data.id)
      if (member) {
        member.isChecked = false
      }
    },
    onMemberCheck (data) {
      // TODO: 待优化
      if (this.multiple) {
        if (data.isChecked) {
          const currentMember = this.selectedMembers.find(t => t.id === data.id) // eslint-disable-line
          const member = {
            id: data.id,
            key: data.key,
            name: data.name || data.label,
            avatar: data.avatar,
            email: data.email,
            phone: data.phone,
            dept: data.dept,
            isHover: false
          }
          if (this.limit - this.disabledUserIds.length - this.selectedMembers.length <= 0) {
            sendMessage(`最多只能添加${this.limit}个`, 'warning')
          } else {
            this.selectedMembers.unshift(member)
          }
        } else {
          this.selectedMembers = this.selectedMembers.filter(t => t.id !== data.id)
        }
      } else {
        const currentMember = this.selectedMembers.find(t => t.id === data.id)
        if (data.isChecked) {
          const member = {
            id: data.id,
            name: data.name || data.label,
            avatar: data.avatar,
            email: data.email,
            phone: data.phone,
            dept: data.dept,
            isHover: false
          }
          if (!currentMember) {
            if (!this.selectedMembers.length) {
              this.selectedMembers.unshift(member)
            } else {
              data.isChecked = !data.isChecked
              sendMessage('只能选择一个用户', 'warning')
            }
          }
        } else if (!data.isChecked && currentMember) {
          this.selectedMembers = []
        }
      }
    },
    onOk () {
      this.$emit('onOk', this.selectedMembers)
      this.isVisible = false
    },
    onCancel () {
      this.$emit('onCancel', false)
    },
    handleOnOpen () {
      const _self = this
      _self.$nextTick(() => {
        _self.$refs.searchInput.focus()
      })
      _self.$emit('onOpen')
    }
  }
}
</script>

<style lang="scss" scoped>
.el-dialog.dialog-dept {
  .dept-body {
    overflow: hidden;
  }
  .dept-wrap {
    width: 280px;
    float: left;
    border-right: 1px solid #E8E8E8;
    padding-left: 10px;
    height: 452px;
    .search-form {
      position: relative;
      padding-right: 30px;
      margin-bottom: 15px;
      .el-input__inner {
        padding-right: 22px;
      }
      .clear-action {
        width: 24px;
        height: 32px;
        line-height: 32px;
        position: absolute;
        top: 0;
        right: 32px;
        text-align: center;
        &:hover {
          cursor: pointer;
        }
        i {
          color: #C0C4CC;
        }
      }
    }
    .search-input {
      .el-input__inner {
        height: 32px;
        line-height: 32px;
      }
      .el-input__prefix {
        top: -4px;
      }
    }
  }
  .dept-content {
    margin-right: 3px;
  }
}
</style>
